import { getClosest, each, isHTMLElement, toArray } from "../../../core/js/utilities";
import { prefix } from "../../../core/design-tokens/meta";
import { reducers, actions, createStore, renderState } from "../../../core/js/state-machine/index";
import { getScreenBreakpointInstance } from "../../../core/js/state-machine/global-resizer";
import {
    componentName,
    defaultProps,
    eventMap,
    initialState,
    sideNavClasses,
    views,
} from "../../../core/components/side-nav/side-nav";
import SideNavGroup from "./side-nav-group";
import SideNavItem from "./side-nav-item";
import eventDispatcher from "../../../core/js/state-machine/events";
import validateInitialization from "../../../core/js/helpers/validate-initialization";

const SideNav = (element, props = {}) => {
    const [success, mergedProps] = validateInitialization({
        componentName,
        defaultProps,
        element,
        props,
    });
    if (!success) return;

    // Use the screenBreakpointInstnace instance and if there isn't one, create one for the application and use it.
    const initialBreakpointState = getScreenBreakpointInstance().getState();

    /**
     * When we resize the element we may need to collapse/expand the component based on media query breakpoints.
     */
    const handleResizeEvent = (state) => {
        if (mergedProps.fixed) {
            // If our state is fixed - we only want to collapse on mobile view
            state.xs || state.sm
                ? toggleStore.dispatch(actions.collapse())
                : toggleStore.dispatch(actions.expand());

            eventDispatcher(eventMap[actions.resize().type], element);
            return;
        }

        //Collapse when viewport is mobile/tablet or when user wants a default collapsed side nav
        state.xs || state.sm || state.md || mergedProps.expand === false
            ? (toggleStore.dispatch(actions.collapse()),
              eventDispatcher(eventMap[actions.resize().type], element))
            : (toggleStore.dispatch(actions.expand()),
              eventDispatcher(eventMap[actions.resize().type], element));
    };

    /**
     * When we want to collapse every menu item within the side nav and just show a "rail".
     */
    const collapseAllNavGroups = () => {
        return each(groups, (group) => {
            return group.close();
        });
    };

    /**
     * Determine our current path based on the window object. Sanitize it and return it.
     * @returns {string}
     */
    const getCurrentPath = () => {
        return sanitizePath(window.location.pathname);
    };

    /**
     * When we want to highlight the current active nav item, we must look through the side-nav to
     * find a match of the path.
     * @param {string} path
     */
    const activateCurrentNavItem = (path) => {
        if (typeof path !== "string") return console.warn("'path' must be of type string.");

        let counter = 0;
        while (counter < items.length) {
            const anchor = items[counter].element;
            // Sanitize the anchor's path and test for a match.
            if (path === sanitizePath(anchor.getAttribute("href"))) {
                anchor.parentElement.classList.add(sideNavClasses.itemSelected);
                anchor.setAttribute("active", "");
                activateParentNavGroup(anchor.parentElement.parentElement);
                break;
            }
            counter++;
        }
    };

    /**
     * Look through the elements ancestors for highest level group that is not expanded and select it.
     * @param {HTMLElement} element
     * @returns
     */
    const activateParentNavGroup = (element) => {
        const closestParentGroup = getClosest(
            element.parentElement,
            `.${prefix}side-nav__group`,
            true
        );

        if (!closestParentGroup) return;

        // Get the first child and test to see if the button is expanded. If it is, don't apply
        // selected styling.
        if (closestParentGroup.children[0].getAttribute("aria-expanded") === "true") return;

        // Style the parent group with the selected tag.
        closestParentGroup.classList.add(sideNavClasses.groupSelected);

        // See if the parent has a parent group!
        activateParentNavGroup(closestParentGroup);
    };

    /**
     * Make a path safe to compare without having to worry about `/` at the end.
     * @param {string} path
     * @returns
     */
    const sanitizePath = (path) => {
        if (!path || typeof path !== "string") return console.warn("path must be of type string.");

        let cleanPath = path;
        try {
            // Make the path URL Safe
            cleanPath = decodeURIComponent(cleanPath);
            // Remove any trailing slashes.
            cleanPath.replace(/\/$/, "");
        } catch (e) {
            // Do nothing for now.
        }

        return cleanPath;
    };

    const select = (item) => {
        if (!isHTMLElement(item)) {
            console.error("Provided side navigation item element can not be selected");
            return;
        }
        // Remove any other selected item
        element.querySelectorAll(`.${sideNavClasses.itemSelected}`).forEach((selected) => {
            selected.classList.remove(sideNavClasses.itemSelected);
        });
        element.querySelectorAll(`.${sideNavClasses.groupSelected}`).forEach((selected) => {
            selected.classList.remove(sideNavClasses.groupSelected);
        });
        activateParentNavGroup(item);
        item.classList.add(sideNavClasses.itemSelected);
    };

    /**
     * When a toggle button is clicked - we want to toggle the state.
     * @returns
     */
    const handleToggle = () => {
        return (event) => {
            event.preventDefault();
            const userIsTabbing = document.body.classList.contains("user-is-tabbing");
            const isExpanded = event.target.getAttribute("aria-label") === "expand";
            const toggleButton =
                userIsTabbing && isExpanded
                    ? event.target.parentElement.children[0]
                    : event.target.parentElement.children[1];
            toggleStore.dispatch(actions.toggle());
            setTimeout(() => {
                return toggleButton.focus();
            });
        };
    };

    /**
     * When our toggle state changes in the store we should handle them.
     * @param {boolean} newState of the toggles
     */
    const handleTogglesStateChange = (newState) => {
        if (!newState) collapseAllNavGroups();
        renderState(newState ? views.expanded : views.collapsed, element);
        const eventName = newState ? actions.expand() : actions.collapse();
        eventDispatcher(eventMap[eventName.type], element);
    };

    /**
     * Loop through our toggles and activate the listener.
     */
    const activateToggles = () => {
        for (let i = 0; i < toggles.triggers.length; i++) {
            toggles.listeners[i] = handleToggle();
            toggles.triggers[i].addEventListener("click", toggles.listeners[i]);
        }
    };

    /**
     * Cleanup method to reset our component.
     */
    const dispose = () => {
        toggles.triggers = [];
        toggles.listeners = [];
        each(groups, (group) => {
            return group.dispose();
        });
        each(items, (item) => {
            return item.dispose();
        });
    };

    // If we are on mobile or tablet - by default don't expand.
    if (initialBreakpointState.xs || initialBreakpointState.sm || initialBreakpointState.md) {
        initialState.expand = false;
        renderState(views.collapsed, element);
    }

    if (mergedProps.expand === false) {
        initialState.expand = false;
        renderState(views.collapsed, element);
    }

    // Read in the HTML property keys to modify the state accordingly.
    if (mergedProps.fixed || element.classList.contains(sideNavClasses.fixed)) {
        initialState.expand = true;
        renderState(views.fixed, element);
    }

    const toggles = {
        triggers: [],
        listeners: [],
    };
    let groups = [];
    let items = [];

    toggles.triggers = toArray(element.querySelectorAll(sideNavClasses.toggleButton));
    groups = toArray(element.querySelectorAll(`.${sideNavClasses.group}`));
    items = toArray(element.getElementsByTagName("A"));

    const toggleStore = createStore(reducers.boolReducer, initialState.expand);
    toggleStore.subscribe(handleTogglesStateChange);

    getScreenBreakpointInstance().subscribe(handleResizeEvent);

    // Set up listeners for our component to respond to from the masthead.
    // TODO - once masthead is done - figure out what to do with these.
    element.addEventListener("uicMastheadFlyoutOpened", () => {
        toggleStore.dispatch(actions.expand());
    });
    element.addEventListener("uicMastheadFlyoutClosed", () => {
        toggleStore.dispatch(actions.collapse());
    });

    // If we are not fixed - activate the toggles.
    if (mergedProps.fixed !== true) activateToggles();

    // Make child components
    groups = groups.map((group, index) => {
        return SideNavGroup(group, {
            index,
            store: {
                toggleStore,
            },
        });
    });

    items = items.map((item, index) => {
        return SideNavItem(item, {
            index,
            store: {
                toggleStore,
            },
        });
    });

    handleResizeEvent(initialBreakpointState);

    // Select our current item based on path.
    activateCurrentNavItem(getCurrentPath());

    const api = {
        element,
        dispose,
        expand: () => {
            toggleStore.dispatch(actions.expand());
        },
        collapse: () => {
            toggleStore.dispatch(actions.collapse());
        },
        toggle: () => {
            toggleStore.dispatch(actions.toggle());
        },
        select,
    };

    // Assign ourselves to the element.
    element[componentName] = api;

    // Return ourselves incase we are captured in js.
    return api;
};

export default SideNav;
